/**
 * Bundle version check — auto-reload stale tabs after a new deploy.
 *
 * Problem this solves:
 * The frontend is auto-deployed on every push to `main`. Users who keep their
 * tab open across deploys (typically residences whose tab sits open for hours
 * before a visit starts) run an older JS bundle. With recent CI fixes shipping
 * critical RTM/chat patches, these stale tabs experience bugs the deployed
 * code no longer has.
 *
 * Mechanism:
 * - At build time, the CI exports `REACT_APP_BUILD_ID` (typically the short
 *   commit SHA). Webpack inlines it into the bundle and writes a sibling
 *   `build/version.json` containing the same value.
 * - At runtime, the app periodically GETs `/version.json` with a cache-bust
 *   and compares the server value to the embedded value.
 * - If they differ, the user is on a stale bundle:
 *     - If they are OUTSIDE an active visit (`/meeting/*` or `/handlenative/*`)
 *       → reload immediately.
 *     - If they are INSIDE an active visit → defer reload until they leave.
 *
 * Safe no-op when:
 * - `REACT_APP_BUILD_ID` is unset (dev / older builds)
 * - `version.json` is missing or returns non-JSON
 * - The fetch fails (offline, transient network)
 */
import * as Sentry from "@sentry/react";

const EMBEDDED_BUILD_ID = process.env.REACT_APP_BUILD_ID || "";
const POLL_INTERVAL_MS = 30 * 60 * 1000; // 30 min — light, won't hammer nginx
const VERSION_URL = "/version.json";

let pollTimer: ReturnType<typeof setInterval> | null = null;
let staleDetected = false;

interface VersionInfo {
  buildId?: string;
  builtAt?: string;
}

function isInActiveVisit(): boolean {
  const path = window.location.pathname;
  return path.startsWith("/meeting/") || path.startsWith("/handlenative/");
}

async function fetchServerBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionInfo;
    return data?.buildId || null;
  } catch {
    return null;
  }
}

async function checkVersionAndMaybeReload(): Promise<void> {
  if (!EMBEDDED_BUILD_ID) return;
  const serverBuildId = await fetchServerBuildId();
  if (!serverBuildId || serverBuildId === EMBEDDED_BUILD_ID) return;

  if (!staleDetected) {
    staleDetected = true;
    console.warn("[version-check] stale bundle detected", {
      embedded: EMBEDDED_BUILD_ID,
      server: serverBuildId,
    });
    Sentry.addBreadcrumb({
      category: "version-check",
      message: "stale bundle detected",
      data: { embedded: EMBEDDED_BUILD_ID, server: serverBuildId },
      level: "info",
    });
  }

  if (isInActiveVisit()) {
    console.log("[version-check] in active visit — deferring reload");
    return;
  }

  console.log("[version-check] reloading to pick up new build");
  window.location.reload();
}

export function startVersionCheck(): void {
  if (!EMBEDDED_BUILD_ID) {
    console.log("[version-check] disabled — no REACT_APP_BUILD_ID embedded");
    return;
  }
  console.log("[version-check] enabled — embedded buildId:", EMBEDDED_BUILD_ID);
  checkVersionAndMaybeReload();
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(checkVersionAndMaybeReload, POLL_INTERVAL_MS);

  // Also re-check when the tab becomes visible again after being backgrounded —
  // catches the common case "tab idle for hours, user comes back, new deploy
  // happened in between".
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkVersionAndMaybeReload();
    }
  });
}

export function stopVersionCheck(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

export function isStaleBundle(): boolean {
  return staleDetected;
}
